import { Session } from '@uplift/api';
import { useAddAddendumForSessionMutation, useAllDiagnosisQuery } from '@uplift/queries';
import { useAlert } from '../../../../AlertProvider';
import * as Yup from 'yup';

export type WhatNeedsUpdating = 'clarify' | 'cpt' | 'dx';

export type AddendumFormType = {
  content: string;
  whatNeedsUpdating: WhatNeedsUpdating[];
  primaryCptCodeId: string | null;
  addonCptCodeIds: string[];
  cptCodeChangeReason: string;
  diagnosisIds: string[];
  diagnosisCodeChangeReason: string;
};

export const useAddendumForm = (session: Session) => {
  const snackbar = useAlert();
  const addAddendumMutation = useAddAddendumForSessionMutation();
  const { data: diagnoses = [] } = useAllDiagnosisQuery();
  const fCodeDiagnosisIds = new Set(diagnoses.filter(d => d.code.startsWith('F')).map(d => d.id));

  const initialValues: AddendumFormType = {
    content: '',
    whatNeedsUpdating: [ 'clarify' ],
    primaryCptCodeId: session.cptCodeId ?? null,
    addonCptCodeIds: session.addonCptCodeIds ?? [],
    cptCodeChangeReason: '',
    diagnosisIds: session.diagnosisIds ?? [],
    diagnosisCodeChangeReason: '',
  };

  const validationSchema = Yup.object({
    content: Yup.string().required('Please specify additions or changes'),
    cptCodeChangeReason: Yup.string().when('whatNeedsUpdating', {
      is: (whatNeedsUpdating: WhatNeedsUpdating[]) => whatNeedsUpdating.includes('cpt'),
      then: schema => schema.required('Please enter a code change reason'),
    }),
    diagnosisCodeChangeReason: Yup.string().when('whatNeedsUpdating', {
      is: (whatNeedsUpdating: WhatNeedsUpdating[]) => whatNeedsUpdating.includes('dx'),
      then: schema => schema.required('Please enter a code change reason'),
    }),
    diagnosisIds: Yup.array().of(Yup.string()).when('whatNeedsUpdating', {
      is: (whatNeedsUpdating: WhatNeedsUpdating[]) => whatNeedsUpdating.includes('dx'),
      then: schema => schema
        .min(1, 'Please add at least one diagnosis')
        .test('has-f-code', 'At least one F code diagnosis is required', ids => (ids ?? [])
          .filter((id): id is string => Boolean(id))
          .some(id => fCodeDiagnosisIds.has(id))),
    }),
  });

  const onSubmit = async (values: AddendumFormType) => {
    const cptActive = values.whatNeedsUpdating.includes('cpt');
    const diagnosisActive = values.whatNeedsUpdating.includes('dx');

    try {
      await addAddendumMutation.mutateAsync({
        sessionId: session.id,
        content: values.content,
        primaryCptCodeId: cptActive ? values.primaryCptCodeId : null,
        addonCptCodeIds: cptActive ? values.addonCptCodeIds : null,
        cptCodeChangeReason: cptActive ? values.cptCodeChangeReason : null,
        diagnosisIds: diagnosisActive ? values.diagnosisIds : null,
        diagnosisCodeChangeReason: diagnosisActive ? values.diagnosisCodeChangeReason : null,
      });
      snackbar.success('Addendum added');
    } catch (error) {
      snackbar.error('Could not add addendum');
    }
  };

  return { initialValues, validationSchema, onSubmit };
};
